<template>
  <v-container fluid>
    <v-form @submit.prevent="submit" validate-on="input">
      <v-card elevation="1" class="pa-4 pt-6 mt-n4 ml-n4">
        <v-row>
          <v-col cols="6">
            <v-text-field
              autofocus
              v-model="getVAVState.friendlyName"
              label="VAV Friendly Name"
              :rules="[(v) => !!v || 'Name is required']"
              required
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col sm="6">
            <SiteLocation
              :siteId="siteId"
              :selectedSiteLocationId="getVAVState.siteLocationId"
              :controlType="'VAV'"
              @sitelocationselected="siteLocationSelected"
            />
          </v-col>
          <v-col sm="6">
            <v-autocomplete
              :items="equipmentsList"
              :loading="isEquipmentsLoading"
              :disabled="!equipmentsList.length"
              item-value="equipmentId"
              item-title="equipmentName"
              clearable
              :model-value="getVAVState.equipmentId"
              @update:model-value="(item) => (getVAVState.equipmentId = item)"
              label="Equipment"
              persistent-hint
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
        <v-row class="mt-n4 ml-n5">
          <v-col cols="6">
            <v-col>
              <v-btn type="submit" color="primary" :loading="isLoading">
                Save
              </v-btn>
              <v-btn
                class="ml-5"
                @click="router.push({ name: 'VAVsPortfolio' })"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-col>
        </v-row>
      </v-card>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, inject, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { ToastService } from '@/plugins/toasts';
import { ToastOptions } from '@/Core.UI.Domain/types/Toast';

import SiteLocation from '@/modules/site/site_location/_components/SiteLocation.vue';

import API from '@/Core.Service.Domain/Controls/VAVs/API/index.ts';
import EquipmentAPI from '@/modules/site/equipment/_api';

import { IVAV } from '@/Core.Service.Domain/Controls/VAVs/types/VAV.ts';
import { IEquipment } from '@/Core.Service.Domain/Controls/Controls.Common/types/Equipment.ts';

import { keepSelectedKeys } from '@/Core.Patterns/Factory';

const equipmentsList = ref<IEquipment[]>([]);
const isEquipmentsLoading = ref(false);
const equipmentId = ref<Partial<IVAV>>();
onMounted(async () => {
  try {
    isEquipmentsLoading.value = true;
    equipmentsList.value = await EquipmentAPI.getEquipmentForSite(
      getVAVState.value.siteId
    );
    equipmentId.value = await API.getVAVById(getVAVState.value.deviceId);
    if (equipmentId.value && equipmentId.value.equipmentId) {
      getVAVState.value.equipmentId = equipmentId.value.equipmentId;
    }
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isEquipmentsLoading.value = false;
  }
});

const router = useRouter();

const store = useStore();
const getVAVState = computed<IVAV>(
  () => store.getters['ControlsStore/VAVsStore/getEditVAV']
);

const route = useRoute();
const siteId = computed(() => route.params.siteId);

const siteLocationSelected = (siteLocationId: { id: number }) => {
  getVAVState.value.siteLocationId = siteLocationId.id;
};

const toast = inject<ToastService>('toast')!;
const isLoading = ref(false);

const sanitizedVAV = ref<Partial<IVAV>>();
const submit = async (
  event: PromiseLike<{ valid: boolean }> | { valid: boolean }
) => {
  const { valid } = await event;
  if (!valid) {
    return Promise.reject({
      Error: 'Please fill in all required fields',
    });
  }

  isLoading.value = true;
  try {
    sanitizedVAV.value = keepSelectedKeys(
      getVAVState.value as unknown as Record<string, unknown>,
      ['deviceId', 'friendlyName', 'siteLocationId', 'equipmentId', 'isActive']
    );

    await API.updateVAVById(sanitizedVAV.value as IVAV);
    const options: ToastOptions = {
      title: 'Success',
      message: `VAV settings successfully updated.`,
      status: 'success',
    };
    toast.show(options);
    router.push({ name: 'VAVsPortfolio' });
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};
</script>
